import { supabase } from '../client';
import type { Database } from '../generated';

type GeneratedStoreRow = Database['core']['Tables']['stores']['Row'];

/**
 * Stores read by the operator. Extends the generated row with the
 * archive-lifecycle columns added in
 * `20260613000300_core_store_archive_lifecycle.sql` (the generated types
 * are not yet refreshed for those columns).
 */
export interface Store extends GeneratedStoreRow {
  archived_reason: string | null;
  reactivate_unlock_at: string | null;
  perma_lock_at: string | null;
  archive_warning_sent_at: string | null;
}

export interface ListStoresOptions {
  /**
   * When true, archived stores are returned alongside active ones. The
   * operator app needs this on Settings -> Store so org admins can see
   * their own archived stores and reactivate them. Defaults to false.
   */
  includeArchived?: boolean;
}

export async function listStoresForOrg(
  orgId: string,
  options: ListStoresOptions = {},
): Promise<Store[]> {
  // Stores per org are usually small (one to a few dozen). Capping at
  // 500 covers the largest realistic multi-store enterprise tenant.
  let query = supabase()
    .schema('core')
    .from('stores')
    .select('*')
    .eq('org_id', orgId)
    .order('name', { ascending: true })
    .limit(500);
  if (!options.includeArchived) {
    query = query.is('archived_at', null);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as Store[];
}

/**
 * Create a new store under an org the caller owns or admins.
 *
 * Backs onto the SECURITY DEFINER RPC `core.rpc_create_store`
 * (`supabase/migrations/20260612050000_core_rpc_create_store.sql`).
 * `core.stores` has RLS enabled + forced with no INSERT policy, so this
 * is the only client path that can create stores after onboarding.
 *
 * The per-plan stores cap is enforced by the BEFORE INSERT trigger
 * `billing.fn_stores_quota_guard`. Hitting the cap surfaces as a
 * PostgresError with SQLSTATE 'PC001' that the operator app's
 * `QuotaUpsellProvider` catches and turns into an "Upgrade plan" modal.
 */
export async function createStore(
  orgId: string,
  input: { name: string; slug: string; timezone: string },
): Promise<string> {
  // The generated `Database` type is not yet refreshed for RPC arity changes,
  // so we narrow to `any` at the .schema() call site. Same pattern as
  // `fulfillmentSchema` / `coreSchema` in `packages/db/src/services/fulfillment.ts`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase() as any).schema('core').rpc('rpc_create_store', {
    p_org_id: orgId,
    p_name: input.name,
    p_slug: input.slug,
    p_timezone: input.timezone,
  });
  if (error) throw error;
  return data as string;
}

export async function updateStoreTimezone(
  storeId: string,
  timezone: string,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any).schema('core').rpc('rpc_update_store_timezone', {
    p_store_id: storeId,
    p_timezone: timezone,
  });
  if (error) throw error;
}

/**
 * Archive an active store. Sets a 24-hour reactivation cooldown and a
 * 30-day perma-lock deadline. The corresponding SECURITY DEFINER RPC
 * `core.rpc_archive_store` refuses if it would leave the org with zero
 * active stores, and writes the optional `reason` to
 * `core.stores.archived_reason`.
 */
export async function archiveStore(
  storeId: string,
  reason: string | null = null,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any).schema('core').rpc('rpc_archive_store', {
    p_store_id: storeId,
    p_reason: reason,
  });
  if (error) throw error;
}

/**
 * Reactivate an archived store. Fails inside the 24-hour cooldown,
 * after the 30-day perma-lock, and when the per-plan active-store cap
 * is exceeded (PC001 surfaced through QuotaUpsellProvider).
 */
export async function reactivateStore(storeId: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any).schema('core').rpc('rpc_reactivate_store', {
    p_store_id: storeId,
  });
  if (error) throw error;
}

/**
 * Platform-admin: bypass the 24-hour cooldown and the 30-day perma-lock
 * window to reactivate a store. Pass `bypassQuota = true` to also bypass
 * the per-plan active-store cap.
 */
export async function adminForceReactivateStore(
  storeId: string,
  options: { bypassQuota?: boolean } = {},
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_admin_force_reactivate_store', {
      p_store_id: storeId,
      p_bypass_quota: Boolean(options.bypassQuota),
    });
  if (error) throw error;
}

/**
 * Platform-admin: push out the 30-day perma-lock deadline on an archived
 * store. Resets the warning-email marker when the new window is more
 * than 7 days out so the warning fires again on the new deadline.
 */
export async function adminExtendStoreArchive(
  storeId: string,
  newPermaLockAt: Date,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_admin_extend_store_archive', {
      p_store_id: storeId,
      p_new_perma_lock_at: newPermaLockAt.toISOString(),
    });
  if (error) throw error;
}
